import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import BarChartIcon from '@mui/icons-material/BarChart'
import CloseIcon from '@mui/icons-material/Close'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import { palettePrimaryMutedSurface } from '@/theme/designTokens'

type Props = Readonly<{
  open: boolean
  completedResponses: number
  confirming?: boolean
  onClose: () => void
  /** `null` = resume without a response limit. */
  onConfirm: (additionalResponseTarget: number | null) => void | Promise<void>
}>

const FIELD_ID = 'resume-survey-additional-target'
const HELPER_ID = `${FIELD_ID}-helper`
const HELPER_TEXT =
  'Add an additional response target, or leave this blank to continue the survey without a target.'
const TARGET_ERROR = 'Enter a whole number of at least 1, or leave this blank'

function digitsOnly(value: string): string {
  return value.replaceAll(/\D/g, '')
}

const ACTION_BUTTON_SX = {
  flex: 1,
  height: 48,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 15,
  borderRadius: '12px',
} as const

/** BFP-1129 — resume a Sign-up Survey that was auto-paused at its response target. */
export function ResumeSurveyDialog({
  open,
  completedResponses,
  confirming = false,
  onClose,
  onConfirm,
}: Props) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValue('')
      setError(null)
    }
  }, [open])

  const trimmed = value.trim()
  const confirmLabel = trimmed ? 'Resume With Additional Target' : 'Resume'

  const handleConfirm = () => {
    if (!trimmed) {
      void Promise.resolve(onConfirm(null))
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isInteger(parsed) || parsed < 1) {
      setError(TARGET_ERROR)
      return
    }
    void Promise.resolve(onConfirm(parsed))
  }

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      data-testid="resume-survey-dialog"
      aria-labelledby="resume-survey-title"
      PaperProps={{ sx: { borderRadius: '16px', p: { xs: 2.5, sm: 3 } } }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={confirming}
        data-testid="resume-survey-close"
        sx={{ position: 'absolute', right: 12, top: 12, color: 'text.primary' }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: palettePrimaryMutedSurface,
          color: 'primary.main',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <TrackChangesIcon sx={{ fontSize: 28 }} />
      </Box>

      <Typography
        id="resume-survey-title"
        component="h2"
        sx={{ mt: 2, fontSize: 24, fontWeight: 700, color: 'text.primary' }}
      >
        Resume Survey
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>
        You can optionally add an additional response target to resume this survey.
      </Typography>

      <Box
        role="status"
        data-testid="resume-survey-completed"
        sx={{
          mt: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: '12px',
          bgcolor: palettePrimaryMutedSurface,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            color: 'primary.main',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <BarChartIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Completed</Typography>
          <Typography
            data-testid="resume-survey-completed-count"
            sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}
          >
            {completedResponses.toLocaleString('en-US')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            responses collected so far
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Typography
          component="label"
          htmlFor={FIELD_ID}
          sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}
        >
          Additional Response Target{' '}
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
            (Optional)
          </Box>
        </Typography>
        <InputBase
          id={FIELD_ID}
          fullWidth
          value={value}
          onChange={(e) => {
            setValue(digitsOnly(e.target.value))
            setError(null)
          }}
          placeholder="500"
          disabled={confirming}
          error={Boolean(error)}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            autoComplete: 'off',
            'aria-invalid': Boolean(error),
            'aria-describedby': HELPER_ID,
            'data-testid': 'resume-survey-additional-target-input',
          }}
          sx={{
            mt: 1,
            px: 1.5,
            py: 1,
            fontSize: 14,
            borderRadius: '12px',
            border: '1.5px solid',
            borderColor: error ? 'error.main' : 'text.primary',
            '&.Mui-focused': { borderColor: error ? 'error.main' : 'primary.main' },
            '& input::placeholder': { color: 'text.disabled', opacity: 1 },
          }}
        />
        <Typography
          id={HELPER_ID}
          sx={{
            mt: 0.75,
            fontSize: 12,
            fontWeight: error ? 600 : 400,
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {error ?? HELPER_TEXT}
        </Typography>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={confirming}
          data-testid="resume-survey-cancel"
          sx={{ ...ACTION_BUTTON_SX, borderColor: 'divider', color: 'text.primary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirming}
          data-testid="resume-survey-confirm"
          sx={ACTION_BUTTON_SX}
        >
          {confirming ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
        </Button>
      </Box>
    </Dialog>
  )
}
